<template>
  <div>
    <v-container       
      class="full-height"
      fluid
    >
      <v-card>
        <v-card-title>Employee Payroll Company Report</v-card-title>
        <v-row class="px-4">
          <v-col
            cols="8"
            class="pt-0"
          >
            <v-data-table 
              v-model="selectedEmployees"
              v-validate="'required'"
              dense
              :error-messages="errors.collect('selectedEmployees')"
              data-vv-name="selectedEmployees"
              data-vv-as="employees"
              :headers="headers"
              :items="filteredEmployees"
              hide-default-footer
              fixed-header
              disable-pagination
              show-select
              item-key="employee_uid"
              item-value="employee_uid"
              class="elevation-2 mb-3"
              height="420px"
            >
              <template v-slot:top>
                <h5 class="font-weight-bold py-2 mb-0 pl-2">
                  Employee Selection:
                </h5>
              </template>
              <template v-slot:item.person.name="{ item }">
                {{ item.person.name }}
                <v-tooltip 
                  v-if="item.end_date"
                  bottom
                >
                  <template v-slot:activator="{ on }">
                    <v-icon
                      class="mr-1"
                      v-on="on"
                    >
                      mdi-information-outline
                    </v-icon>
                  </template>
                  Resign on {{ item.end_date }}
                </v-tooltip>
              </template>
              <template v-slot:item.company_employees="{ item }">
                <div 
                  v-for="ce in item.company_employees"
                  :key="ce.company_employee_uid"
                  :class="ce.is_active_pay_status === 1 ? 'text--primary' : 'red--text'"
                >
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                      <v-icon
                        class="mr-1"
                        v-on="on"
                      >
                        mdi-information-outline
                      </v-icon>
                    </template>
                    Start Date : {{ ce.start_date }}
                    <div v-if="ce.end_date">
                      End Date : {{ ce.end_date }}
                    </div>
                  </v-tooltip>
                  {{ (ce.company || {}).name || '' }}
                  <span v-if="(((ce.latest_cost_center || {}).cost_center) || {}).name">
                    ({{ ce.latest_cost_center.cost_center.name }})
                  </span>
                </div>
              </template>
            </v-data-table>
            <span class="red--text text--lighten-1 caption">{{ errors.first('selectedEmployees') }}</span>
          </v-col>

          <v-col
            cols="4"
            class="pt-0"
          >
            <v-text-field
              v-model="searchInput"
              placeholder="Filter employee by name"
              append-icon="search"
              label="Search Employee"
              hide-no-data
              hide-selected
            />

            <v-text-field
              v-model="searchIC"
              placeholder="Filter employee by IC"
              append-icon="search"
              label="Search Employee IC"
              hide-no-data
              hide-selected
            />            

            <v-autocomplete
              v-model="filters.companies"
              :items="companies"
              item-text="name"
              item-value="id"
              label="Company"
              placeholder="Filter employee by company"
              clearable
              hide-selected
              multiple
              small-chips
              deletable-chips
              chips
            />

            <v-autocomplete
              v-model="filters.positions"
              :items="positions"
              item-text="label"
              item-value="id"
              label="Position"
              placeholder="Filter employee by position"
              clearable
              hide-selected
              multiple
              small-chips
              deletable-chips
              chips
            />

            <v-autocomplete
              v-model="filters.departments"
              :items="departments"
              item-text="name"
              item-value="id"
              label="Department"
              placeholder="Filter employee by department"
              clearable
              hide-selected
              multiple
              deletable-chips
              small-chips
              chips
            />

            <v-autocomplete
              v-model="filters.costCenters"
              :items="filteredCostCentersByCompany"
              item-text="label"
              item-value="id"
              label="Cost Center"
              placeholder="Filter employee by cost center"
              clearable
              hide-selected
              multiple
              small-chips
              deletable-chips
              chips
            />

            <v-card-actions>
              <v-spacer />
              <v-btn
                block
                :loading="progressing"
                color="hrissecondary"     
                class="white--text"     
                @click="generate"
              >
                Generate
              </v-btn>
            </v-card-actions>
          </v-col>
        </v-row>  
      </v-card>
    </v-container>  
  </div>      
</template>
<script>
  export default {
    $_veeValidate: {
      validator: 'new', 
    },
    props: {
      employees: {
        type: Array,
        required: true,
      },
      positions: {
        type: Array,
        required: true,
      },
      departments: {
        type: Array,
        required: true,
      },
      companies: {
        type: Array,
        required: true,
      },
      costCenters: {
        type: Array,
        default: () => [],
      },
    },
    data () {
      return {
        progressing: false,
        searchInput: '',
        searchIC: '',
        filters: {
          companies: '',
          positions: '',
          departments: '',
          costCenters: '',
        },
        selectedEmployees: [],
        headers: [
          {
            text: 'Name',
            align: 'left',
            sortable: true,
            value: 'person.name',
            width: '250px',
          },
          {
            text: 'Company',
            align: 'left',
            sortable: true,
            value: 'company_employees',
          },
          {
            text: 'Department',
            align: 'left',
            sortable: true,
            value: 'department.name',
          },      
        ],
      }
    },
    computed: {
      today () {
        return this.$moment().format('YYYY-MM-DD')
      },
      filteredCostCentersByCompany () {
        const selectedCompanies = this.filters.companies || []

        if (!selectedCompanies.length) return this.costCenters

        return this.costCenters.filter(cc => selectedCompanies.includes(cc.selected_company_uid))
      },
      filteredEmployees () {
        let _this = this
        _this.searchInput = _this.searchInput.trim()
        let primaryFilterEmployees = this.employees.filter(function (employee) {
          return ((employee.person || {}).name || '').toLowerCase().indexOf(_this.searchInput.toLowerCase()) !== -1 
        })

        _this.searchIC = _this.searchIC.trim()
        let secondaryFilterEmployees = primaryFilterEmployees.filter(function (employee) {
          return ((employee.person.identity_card || {}).ic_number || '').toLowerCase().indexOf(_this.searchIC.toLowerCase()) !== -1 ||
            ((employee.person.identity_card || {}).passport_number || '').toLowerCase().indexOf(_this.searchIC.toLowerCase()) !== -1
        })

        let filteredCompanies = this.customFilterCompanies(secondaryFilterEmployees)
        let filteredPositions = this.customFilterPositions(filteredCompanies)
        let filteredDepartments = this.customFilterDepartments(filteredPositions)
        let filteredCostCenters = this.customFilterCostCenters(filteredDepartments)
        return filteredCostCenters
      },
    },
    watch: {
      filteredEmployees (newList) {
        this.$validator.pause()

        const newEmployeeUids = new Set(newList.map(emp => emp.employee_uid))

        this.selectedEmployees = this.selectedEmployees.filter(selectedEmp =>
          newEmployeeUids.has(selectedEmp.employee_uid)
        )

        this.$nextTick(() => {
          this.$validator.resume()
        })
      },
    },
    methods: {
      prepareFormData () {
        return {
          employeeUids: this.getEmployeeUids(),
          costCenterIds: this.filters.costCenters,
        }
      },
      generate () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) return

            this.progressing = true

            this.$api.employeeReports().payrollCompReport(this.prepareFormData())
              .then(res => {
                let blob = new Blob([res.data], { type: res.data.type })
                let link = document.createElement('a')
                link.href = window.URL.createObjectURL(blob)
                link.setAttribute('download', 'Employee Payroll Company Report.xlsx')
                link.click()

                this.progressing = false
                this.$snack({ message: 'Report downloaded successfully' })
              })
              .catch(() => {
                this.progressing = false
                this.$snack({
                  color: 'error',
                  message: 'Failed to generate report. Please contact IT support',
                })
              })
          })
      },

      getEmployeeUids () {
        let employeeUids = []

        this.selectedEmployees.map(item => {
          employeeUids.push(item.employee_uid)
        })

        return employeeUids
      },

      customFilterCompanies (employeesList) {
        if (this.filters.companies.length === 0) return employeesList

        return employeesList.filter(employee => {
          if (employee.company_employees.length <= 0) return 

          let companyEmployees = employee.company_employees.filter(companyEmployee => {
            return companyEmployee.is_active_pay_status === 1 && 
              companyEmployee.start_date <= this.today && 
              (companyEmployee.end_date === null || companyEmployee.end_date >= this.today)
          })

          if (companyEmployees.length > 0) {
            let activeCompanyUids = companyEmployees.map(
              ce => ce.company_uid
            )
  
            return _.intersection(activeCompanyUids, this.filters.companies).length > 0
          }
        })
      },

      customFilterPositions (employeesList) {
        if (this.filters.positions.length === 0) return employeesList

        return employeesList.filter(employee => {
          let positionId = (employee.current_position || {}).position_id
  
          return _.intersection([positionId], this.filters.positions).length > 0
        })
      },

      customFilterDepartments (employeesList) {
        if (this.filters.departments.length === 0) return employeesList

        return employeesList.filter(employee => {
          return _.intersection([employee.department_id], this.filters.departments).length > 0
        })
      },

      customFilterCostCenters (employeesList) {
        if (this.filters.costCenters.length === 0) return employeesList

        const today = this.$moment().format('YYYY-MM-DD')
        const from = today
        const to = today

        return employeesList.filter(employee => {
          const costCenterIds = _.flatMap(employee.company_employees || [], companyEmployee => companyEmployee.company_employee_cost_centers || [])
            .filter(costCenter => (!costCenter.start_date || costCenter.start_date <= to) && (!costCenter.end_date || costCenter.end_date >= from))
            .map(costCenter => costCenter.cost_center_id)

          return _.intersection(costCenterIds, this.filters.costCenters).length > 0
        })
      },
    },
  }
</script>
